'use client';

import { Option } from 'effect';
import { useEffect, useRef, useState } from 'react';

export type ImageFallbackStage = 'primary' | 'fallback' | 'error';

type UseImageFallbackFlowOptions<T> = {
  primary: T;
  fallback: Option.Option<T>;
  resolveFallback?: (img: HTMLImageElement) => Option.Option<T>;
  resetKey?: unknown;
};

export function useImageFallbackFlow<T>({
  primary,
  fallback,
  resolveFallback,
  resetKey = primary,
}: UseImageFallbackFlowOptions<T>) {
  const [stage, setStage] = useState<ImageFallbackStage>('primary');
  const [selectedFallback, setSelectedFallback] = useState<Option.Option<T>>(Option.none());
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setSelectedFallback(Option.none());
    setStage('primary');
  }, [resetKey]);

  function getFallback(img: HTMLImageElement): Option.Option<T> {
    const resolvedFallback = resolveFallback?.(img) ?? Option.none<T>();
    return Option.orElse(resolvedFallback, () => fallback);
  }

  function activateFallback(img: HTMLImageElement | null): void {
    const nextFallback = img === null ? fallback : getFallback(img);

    Option.match(nextFallback, {
      onNone: () => {
        setStage('error');
      },
      onSome: (fallback) => {
        setSelectedFallback(Option.some(fallback));
        setStage('fallback');
      },
    });
  }

  // SSR hydration race: the <img> may have already fired `error` before React
  // attached the onError handler. Only inspect the primary stage; the regular
  // onError handler is the legitimate path out of fallback.
  useEffect(() => {
    if (stage !== 'primary') return;
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth === 0) {
      activateFallback(img);
    }
  }, [fallback, resetKey, resolveFallback, stage]);

  const current =
    stage === 'fallback' ? Option.getOrElse(selectedFallback, () => primary) : primary;

  return {
    stage,
    current,
    imgRef,
    isError: stage === 'error',
    handleError: (img: HTMLImageElement | null = imgRef.current) => {
      if (stage === 'primary') {
        activateFallback(img);
        return;
      }

      setStage('error');
    },
  };
}
